import { useEffect, useRef, useState } from "react";
import flattenAudioChunks from "../functions/flattenAudioChunks";
import encodeAudioToMp3 from "../functions/encodeAudioToMp3";
import useTimer from "./useTimer";

const useRotatingMicRecorder = ({
  onStop,
  bufferDuration = 2000,
  autoStart = false,
}) => {
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const scriptProcessorRef = useRef(null);
  const circularBufferRef = useRef([]);
  const isRecordingRef = useRef(false);
  const recordedChunksRef = useRef([]);
  const [isRecording, setIsRecording] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  const timer = useTimer();

  // Start recording and buffering
  const start = async () => {
    audioContextRef.current = new AudioContext({ sampleRate: 44100 });
    mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    setIsRecording(true);
    setAudioStream(mediaStreamRef.current);
    const source = audioContextRef.current.createMediaStreamSource(
      mediaStreamRef.current
    );

    const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);
    source.connect(processor);
    processor.connect(audioContextRef.current.destination);

    scriptProcessorRef.current = processor;

    const maxFrames = Math.floor(
      bufferDuration / ((4096 / audioContextRef.current.sampleRate) * 1000)
    );
    circularBufferRef.current = [];

    processor.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);
      const inputData = new Float32Array(input);
      circularBufferRef.current.push(inputData);
      if (circularBufferRef.current.length > maxFrames) {
        circularBufferRef.current.shift(); // keep buffer rotating
      }
      if (isRecordingRef.current) {
        recordedChunksRef.current.push(inputData);
      }
    };
  };

  // Begin keeping chunks
  const beginFinalRecording = () => {
    isRecordingRef.current = true;
    timer.startTimer();
    recordedChunksRef.current = [...circularBufferRef.current]; // include buffered pre-trigger audio
  };

  const disableRecorder = async () => {
    isRecordingRef.current = false;

    try {
      // Disconnect script processor
      if (scriptProcessorRef.current) {
        scriptProcessorRef.current.disconnect();
        scriptProcessorRef.current = null;
      }

      // Stop media stream tracks
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
        mediaStreamRef.current = null;
      }

      // Close audio context
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        const audioContext = audioContextRef.current; // Store reference
        audioContextRef.current = null; // Clear ref first
        await audioContext.close(); // Then close
      }

      timer.stopTimer();
      timer.resetTimer();
      setIsRecording(false);
      setAudioStream(null);
    } catch (e) {
      console.error("Failed to clean up audio resources:", e);
    }
  };

  // Finalize
  const stop = async () => {
    const fullAudio = flattenAudioChunks(recordedChunksRef.current);
    const mp3Blob = await encodeAudioToMp3(fullAudio, 44100);
    await disableRecorder(); // ensure it's fully stopped *before* cleanup
    onStop(new File([mp3Blob], "recorded_audio.mp3", { type: "audio/mp3" }));
  };

  useEffect(() => {
    if (autoStart) {
      start();
    }
    return () => {
      disableRecorder();
    };
  }, []);

  return {
    start,
    beginFinalRecording,
    stop,
    disableRecorder,
    isRecording,
    audioStream,
    durationInSeconds: timer.time,
  };
};

export default useRotatingMicRecorder;
